import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://lunaroccultation.com";
const DEFAULT_IMAGE = `${SITE}/og-image.svg`;

/**
 * SEO — per-page meta tags.
 *
 * Props:
 *  - title: page-specific title (will be suffixed with brand)
 *  - description: 150-160 chars ideal
 *  - path: route path (used for canonical + og:url)
 *  - image: absolute URL to OG image override
 *  - type: og:type (default 'website', use 'article' for blog)
 *  - keywords: comma-separated string (optional)
 *  - noindex: boolean — set true for utility pages
 *  - schema: object OR array of JSON-LD schemas to inject
 */
export default function SEO({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  keywords,
  noindex = false,
  schema,
}) {
  const fullTitle = title
    ? `${title} — Sentry by Lunar Occultation`
    : "Sentry by Lunar Occultation — The Governance OS for Regulated AI";
  const url = `${SITE}${path.startsWith("/") ? path : `/${path}`}`;
  const schemas = schema
    ? Array.isArray(schema) ? schema : [schema]
    : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
      }
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Sentry by Lunar Occultation" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
